import { useState } from 'react';
import { FileLanguageType, WorkspaceFileInfo } from '@scale/shared/sphereEngine/types';
import { WorkspaceContainerField } from '@scale/shared/types/params/textcollection';
import { Accordion, Spinner, Typography, HelperText, Icon } from '@scale/scaleui';
import logger from 'react-scale/lib/logging';
import { getHelperText } from 'react-scale/components/scalers/tasks/textcollection/helperText';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import apiFetch from 'react-scale/lib/api-fetch';
import FileSaver from 'file-saver';

const downloadFile = async (cdsUrl: string, fileName: string) => {
  try {
    const { url } = await apiFetch(null, `internal/contentUnderstanding/fileFetch`, {
      method: 'POST',
      body: {
        url: cdsUrl,
      },
    });
    const response = await fetch(url, {
      headers: { 'Content-Type': 'text/plain' },
    });
    if (!response || !response.body) {
      logger.error('Failed to download file');
      return;
    }
    const reader = response.body.getReader();
    const stream = new ReadableStream({
      start(controller) {
        function push() {
          reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                controller.close();
                return; // Properly returning from the `then()`
              }
              controller.enqueue(value);
              return push(); // Return the next call to push()
            })
            .catch(err => {
              logger.error('Stream reading failed:', err);
              controller.error(err); // Report any errors to the stream
              throw err; // Ensure the catch block throws an error
            });
        }
        push();
      },
    });
    const blob = await new Response(stream).blob();
    FileSaver.saveAs(blob, fileName);
  } catch {
    // Try the old way if the new way fails
    const { body } = await apiFetch(null, `internal/contentUnderstanding/fileDownload`, {
      method: 'POST',
      body: {
        url: cdsUrl,
      },
    });
    const blob = new Blob([new Uint8Array(body.data)], {
      type: 'text/plain',
    });
    FileSaver.saveAs(blob, fileName);
  }
};
interface WorkspaceFilesAndOutputProps {
  field: WorkspaceContainerField;
  isFetchingFiles: boolean;
  filesToDisplay: Partial<WorkspaceFileInfo>[] | null;
  stdoutOutput?: string | null;
  stderrOutput?: string | null;
  testStdoutOutput?: string | null;
  testStderrOutput?: string | null;
  stdoutOutputUrl?: string | null;
  stderrOutputUrl?: string | null;
  testStdoutOutputUrl?: string | null;
  testStderrOutputUrl?: string | null;
  shouldMountWorkspaceModal: boolean;
  handleWorkspaceOpen: () => void;
  afterExecutionWaitTime: number;
  disabled?: boolean;
}

// eslint-disable-next-line complexity
function WorkspaceFilesAndOutput({
  field,
  isFetchingFiles,
  filesToDisplay,
  stdoutOutput,
  stderrOutput,
  testStdoutOutput,
  testStderrOutput,
  stdoutOutputUrl,
  stderrOutputUrl,
  testStdoutOutputUrl,
  testStderrOutputUrl,
  shouldMountWorkspaceModal,
  handleWorkspaceOpen,
  afterExecutionWaitTime,
  disabled,
}: WorkspaceFilesAndOutputProps) {
  return (
    <>
      {isFetchingFiles && <Spinner className="" size="sm" variant="primary" />}
      {!isFetchingFiles &&
        (stdoutOutput || stderrOutput || testStdoutOutput || testStderrOutput) && (
          <div className="mt-3 mb-[3px] flex flex-row w-full flex-grow-0">
            <div className="flex gap-1 items-center">
              <HelperText>{getHelperText({ description: 'Files' })}</HelperText>
            </div>
          </div>
        )}
      <div className="grid grid-cols-2 gap-2">
        {!isFetchingFiles &&
          filesToDisplay &&
          filesToDisplay.map(file => {
            if (!file.fileContent || !file.filePath) {
              return null;
            }
            const language = file.fileLanguage;
            return (
              <FileOrOutputDisplay
                key={`${field.field_id}-${file.filePath}`}
                title={file.filePath}
                content={file.fileContent}
                contentUrl={file.contentUrl ?? undefined}
                handleWorkspaceOpen={handleWorkspaceOpen}
                shouldMountWorkspaceModal={shouldMountWorkspaceModal}
                language={language}
                className=""
                afterExecutionWaitTime={afterExecutionWaitTime}
                disabled={disabled}
              />
            );
          })}
        {!isFetchingFiles && stdoutOutput && (
          <FileOrOutputDisplay
            key={`${field.field_id}-stout`}
            title={`Execution output`}
            content={stdoutOutput}
            contentUrl={stdoutOutputUrl ?? undefined}
            className=""
            isOutput
          />
        )}

        {!isFetchingFiles && stderrOutput && (
          <FileOrOutputDisplay
            key={`${field.field_id}-stderr`}
            title={`Error output`}
            content={stderrOutput}
            contentUrl={stderrOutputUrl ?? undefined}
            className=""
            isOutput
          />
        )}

        {!isFetchingFiles && testStdoutOutput && (
          <FileOrOutputDisplay
            key={`${field.field_id}-stdout-test`}
            title={`Execution tests output`}
            content={testStdoutOutput}
            contentUrl={testStdoutOutputUrl ?? undefined}
            className=""
            isOutput
          />
        )}

        {!isFetchingFiles && testStderrOutput && (
          <FileOrOutputDisplay
            key={`${field.field_id}-stderr-test`}
            title={`Error tests output`}
            content={testStderrOutput}
            contentUrl={testStderrOutputUrl ?? undefined}
            className=""
            isOutput
          />
        )}
      </div>
    </>
  );
}

interface FileOrOutputDisplayProps {
  key: string;
  title: string;
  content: string;
  contentUrl?: string;
  className?: string;
  language?: FileLanguageType;
  shouldMountWorkspaceModal?: boolean;
  handleWorkspaceOpen?: () => void;
  afterExecutionWaitTime?: number;
  disabled?: boolean;
  isOutput?: boolean;
}

function FileOrOutputDisplay({
  key,
  title,
  content,
  contentUrl,
  className,
  language,
  isOutput,
}: FileOrOutputDisplayProps) {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div key={key} className={className}>
      <Accordion
        className="shadow-none border border-solid border-neutral-300"
        expanded={expanded}
        onChange={() => {
          setExpanded(prev => !prev);
        }}
        header={
          <div className="flex flex-row justify-between w-full flex-grow-0">
            <div className="flex flex-row">
              <div className="flex gap-1 align-center items-center">
                <Icon
                  className="text-neutral-300"
                  size="md"
                  variant="black"
                  icon={isOutput ? ['far', 'terminal'] : ['far', 'file']}
                />
              </div>
              <div className="flex gap-1 text-neutral-600 items-center align-center">
                <span className="font-inherit text-[12px] font-medium items-center align-center">
                  {title}
                </span>
              </div>
            </div>
            <div className="flex flex-row justify-evenly font-inherit">
              <Icon
                className="flex gap-1 items-center mr-2 border-none shadow-none"
                onClick={e => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(content);
                }}
                size="md"
                variant="black"
                icon={['far', 'copy']}
              />
              {contentUrl && (
                <Icon
                  className="flex gap-1 items-center mr-2 border-none shadow-none"
                  onClick={e => {
                    e.stopPropagation();
                    downloadFile(contentUrl, title);
                  }}
                  size="md"
                  variant="black"
                  icon={['far', 'download']}
                />
              )}
            </div>
          </div>
        }
        target={
          isOutput ? (
            <Typography.Overline1 className="w-auto font-mono text-xs whitespace-pre-wrap h-[500px] overflow-scroll">
              {' '}
              {content}
            </Typography.Overline1>
          ) : (
            <Typography.Overline1 className="w-auto">
              <div className="w-inherit h-[500px] overflow-scroll">
                <ChatMarkdown
                  markdown={`~~~${language ?? ''}\n${content}\n~~~` || ''}
                  enableExecutableCodeBlocks
                />
              </div>
            </Typography.Overline1>
          )
        }
      />
    </div>
  );
}

export default WorkspaceFilesAndOutput;
